import { useState } from "react";
import makeStyles from "@mui/styles/makeStyles";
import createStyles from "@mui/styles/createStyles";
import Grid from "@mui/material/Grid";
import { useAuth } from "../core/context/auth.context";
import { GridColDef, GridRenderCellParams } from "@mui/x-data-grid";
import { useSocket } from "../core/context/socket.context";
import UserDebtsService from "../core/service/user-debts.service";
import UserDebtFilterInterface from "@homegame/common/dist/interface/filters/user-debt-filter.interface";
import { useQueryClient } from "@tanstack/react-query";
import AccumulatedDebtInterface from "@homegame/common/dist/interface/accumulated-debt.interface";
import { useSocketSubscription } from "../core/hooks/use-web-socket-subscription.hook";
import AccumulatedDebtItemComponent from "./components/accumulated-debt-item.component";
import TableComponent from "../core/components/table.component";

const PAGE_SIZE = 5;

const useStyles = makeStyles(() =>
  createStyles({
    wrap: {
      minWidth: "100%",
      paddingLeft: 0,
      height: "100%",
      "& .MuiDataGrid-footerContainer": {
        border: "none",
      },
      "& .MuiDataGrid-cell": {
        border: "none",
        padding: 0,
        minWidth: 200,
        width: "100%",
      },
      "& .MuiDataGrid-columnHeaders": {
        border: "none",
      },
      "& .MuiDataGrid-root ": {
        border: "none",
      },
    },
    table: {
      "& .MuiDataGrid-row": {
        background:
          "linear-gradient(135deg, rgba(255,255,255,0.06), rgba(255,255,255,0.03))",
        backdropFilter: "blur(12px)",
        borderRadius: 12,
        border: "1px solid rgba(255,255,255,0.1)",
        boxShadow: "0 4px 12px rgba(0,0,0,0.15)",
        padding: 10,
        width: "100%",
        "&:hover": {
          background:
            "linear-gradient(135deg, rgba(255,255,255,0.06), rgba(255,255,255,0.03))",
        },
      },
    },

    heading: {
      fontSize: 10,
      width: "100%",
      textShadow: "none",
    },

    btnsContainer: {
      display: "flex",
      justifyContent: "space-between",
      width: "100%",
      paddingTop: 5,
      paddingLeft: 10,
      paddingRight: 10,
      "& button": {
        textShadow: "none",
        minWidth: 80,
      },
    },
  }),
);

interface UserDebtsListProps {
  type?: "users" | "to-user";
  pageSize?: number;
  height?: number | string;
  onDebtChange?: () => void;
  noItemsText?: string;
}

export default function UserDebtsListComponent({
  type,
  pageSize = PAGE_SIZE,
  height,
  onDebtChange,
  noItemsText = "You have no debts",
}: UserDebtsListProps) {
  const classes = useStyles({});
  const userDebtsService = new UserDebtsService();

  const queryClient = useQueryClient();
  const { user } = useAuth();
  const { transactionSocket } = useSocket();

  const [refreshTrigger, setRefreshTrigger] = useState(1);

  let tableFilters: UserDebtFilterInterface = {
    take: pageSize,
    skip: 0,
    status: [],
  };

  if (type === "users") {
    tableFilters.fromUserId = user?.id;
  } else if (type === "to-user") {
    tableFilters.toUserId = user?.id;
  }

  const serializedFilters = JSON.stringify(tableFilters);

  const columns: GridColDef[] = [
    {
      field: "createdAt",
      headerName: "",
      flex: 1,
      colSpan: 2,
      hideable: false,
      sortable: false,
      headerClassName: "hidden",
      renderCell: (params: GridRenderCellParams) => {
        const debt: AccumulatedDebtInterface = params.row;

        return (
          <AccumulatedDebtItemComponent
            debt={debt}
            type={type}
            onAction={handleDebtChange}
          />
        );
      },
    },
  ];

  const updateTable = async (data: any) => {
    if (
      [data.transaction.fromUserId, data.transaction.toUserId].includes(
        user?.id,
      )
    ) {
      await queryClient.invalidateQueries({
        queryKey: ["debtsHistoryItems", serializedFilters],
        exact: true,
      });
    }
  };

  useSocketSubscription(transactionSocket?.connect(), [
    { eventName: "transactionUpdated", callback: updateTable },
    { eventName: "transactionCreated", callback: updateTable },
  ]);

  const getRowId = (row: any) => {
    if (row.isObserverRow) {
      return row.id;
    }
    return (
      row.fromUserId.toString() +
      row.toUserId.toString() +
      row.tableId.toString()
    );
  };

  const handleDebtChange = () => {
    setRefreshTrigger(refreshTrigger + 1);
    setTimeout(() => {
      queryClient.clear();
    }, 300);
    if (onDebtChange) onDebtChange();
  };

  return (
    <Grid item xs={12} className={classes.wrap}>
      <TableComponent
        columns={columns}
        listMethod={userDebtsService.list.bind(userDebtsService)}
        filters={tableFilters}
        height={height}
        className={classes.table}
        refresh={refreshTrigger}
        getRowId={getRowId}
      />
    </Grid>
  );
}
